// Home.js
import React, { useEffect, useState } from "react";
import { Divider, Skeleton } from "@mui/material";
import User_Not_Found from "../../Assets/SignUp/User_Not_Found.png";
import FilterForm from "../../components/FormComponents/FilterForm";
import InputSelecter from "../../components/FormComponents/InputSelecter";
import ProfileCard from "../../components/CardComponets/ProfileCard";
import CustomPagination from "../../components/FormComponents/CustomPagination";
import { BsFilterRight } from "react-icons/bs";
import FilterModal from "../../modal/FilterModal";
import { useDispatch, useSelector } from "react-redux";
import { usersProfileDetails } from "../../Redux/Actions/UsersProfileAction";
import { toast } from "react-toastify";
import useTitle from "../../Hooks/useTitle";
import AnimatedCounter from "../../utils/AnimatedCounter";
import useScrollToTop from "../../Hooks/useScrollToTop";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Selfie from "../../Assets/SignUp/camera.png"

const Home = () => {
  useTitle("Home");
  useScrollToTop();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    gender: "",
    age: "",
    height: "",
    cast: "",
    native_place: "",
    marital_status: "",
    education: "",
  });

  // Set default sort to "Newest"
  const [sortBy, setSortBy] = useState("date_new");

  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem('homeCurrentPage');
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const dispatch = useDispatch();
  const UsersProfile = useSelector((state) => state.UsersProfile || {});

  const [profileData, setProfileData] = useState(null);
  const totalPages = UsersProfile?.usersProfileData?.last_page || 1;

  const [submittedFilters, setSubmittedFilters] = useState({});

  useEffect(() => {
    sessionStorage.setItem('homeCurrentPage', currentPage);
  }, [currentPage]);

  // Check sender_image and show alert if null
  useEffect(() => {
    const profile = UsersProfile?.personalDetails?.data;

    if (!profile) return;

    const isAdmin = profile?.is_admin;
    const isSubAdmin = profile?.role === "sub_admin";
    const senderImage = profile?.other_details?.sender_image;

    console.log("is_admin", isAdmin);
    console.log("sender_image", senderImage);

    if (isAdmin || isSubAdmin) return;

    if (!senderImage) {
      Swal.fire({
        title: 'Selfie Verification Required',
        text: 'Please upload your selfie for verification to complete your profile.',
        imageUrl: Selfie,
        imageWidth: 80,
        imageHeight: 80,
        imageAlt: 'Selfie Required',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Upload Now',
        cancelButtonText: 'Later',
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then((result) => {
        if (result.isConfirmed) {
          // Store the current scroll position for return navigation
          sessionStorage.setItem('homeScrollPosition', window.pageYOffset.toString());
          sessionStorage.setItem('fromVerificationAlert', 'true');
          
          // Navigate to Verification with smooth scroll parameter
          navigate('/profile/Verification?fromAlert=true&smoothScroll=true');
        }
      });
    }
  }, [UsersProfile?.personalDetails?.data, navigate]);

  // Restore scroll position when returning from verification
  useEffect(() => {
    const fromAlert = sessionStorage.getItem('fromVerificationAlert');
    const savedPosition = sessionStorage.getItem('homeScrollPosition');
    
    if (fromAlert === 'true' && savedPosition) {
      // Small delay to ensure the page is fully loaded
      setTimeout(() => {
        window.scrollTo({
          top: parseInt(savedPosition),
          behavior: 'smooth'
        });
        
        // Clean up
        sessionStorage.removeItem('fromVerificationAlert');
        sessionStorage.removeItem('homeScrollPosition');
      }, 300);
    }
  }, []);

  // Fetching profile details on dependency changes
  useEffect(() => {
    const fetchProfileDetails = async () => {
      try {
        await dispatch(
          usersProfileDetails({
            page: currentPage,
            ...submittedFilters,
            sort_by: sortBy,
          })
        );
      } catch (error) {
        toast.error(error?.response?.data?.message);
      }
    };
    fetchProfileDetails();
  }, [dispatch, currentPage, submittedFilters, sortBy]);

  // Update local profile data when UsersProfile changes
  useEffect(() => {
    if (UsersProfile?.usersProfileData?.data) {
      setProfileData(UsersProfile.usersProfileData.data);
    } else {
      setProfileData(null);
    }
  }, [UsersProfile]);

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      gender: "",
      age: "",
      height: "",
      cast: "",
      native_place: "",
      marital_status: "",
      education: "",
    });
    setSubmittedFilters({});
    setCurrentPage(1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSubmittedFilters(formData);
  };

  return (
    <>
      <div className="md:max-w-[750px] lg:max-w-[1100px] xl:max-w-[1200px] 2xl:max-w-[1600px] mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-2/6 xl:w-full 2xl:w-1/5 hidden lg:block xl:hidden 2xl:block">
            <div className="sticky top-4 animate-fade-in-side">
              <FilterForm
                formData={formData}
                handleFormChange={handleFormChange}
                handleSubmit={handleSubmit}
                resetForm={resetForm}
              />
            </div>
          </div>
          <div className="w-full lg:w-4/6 xl:w-full 2xl:w-4/5">
            <div className="flex flex-row items-center justify-between mb-4">
              <h2 className="text-xl font-semibold mb-2 md:mb-0">
                Profiles (<AnimatedCounter total={UsersProfile?.usersProfileData?.total || 0} />)
              </h2>
              <div className="flex items-center gap-2">
                <button
                  className="min-w-max p-2 border border-[#E6E6E6] rounded-lg lg:hidden xl:flex 2xl:hidden"
                  onClick={() => setModalOpen(true)}
                >
                  <BsFilterRight size={20} />
                </button>
                <InputSelecter
                  value={sortBy}
                  onChange={handleSortChange}
                  options={[
                    { label: "Newest", value: "date_new" },
                    { label: "Oldest", value: "date_old" },
                  ]}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 custom-grid gap-3">
              {UsersProfile.loading || !profileData ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="col-span-1">
                    <Skeleton
                      sx={{ borderRadius: "10px" }}
                      variant="square"
                      width="100%"
                      height={200}
                    />
                  </div>
                ))
              ) : profileData.length < 1 ? (
                <div className="w-full col-span-2 flex justify-center items-center py-10 my-10 flex-col">
                  <img src={User_Not_Found} alt="No user found" />
                  <h1 className="text-center font-bold text-2xl my-3">No Profiles Match Your Search</h1>
                  <p className="text-gray-600 text-center mb-4">
                    Try adjusting your filters or check back later for new profiles.
                  </p>
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary-dark transition"
                  >
                    Reset All Filters
                  </button>
                </div>
              ) : (
                profileData.map((profile) => (
                  <ProfileCard key={profile?.id} profile={profile} />
                ))
              )}
            </div>
            <div className="flex justify-center mt-6">
              {profileData?.length > 0 && (
                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <FilterModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        filterProps={{
          formData,
          handleFormChange,
          handleSubmit: (e) => {
            handleSubmit(e);
            setModalOpen(false);
          },
          resetForm,
        }}
      />
    </>
  );
};

export default Home;